import { userService } from 'app/services/userService';
import { useSessionStore } from 'app/stores/globalStore';
import { useImpersonationStore } from 'app/stores/impersonationStore';
import { useToken } from 'app/utils/token';
import useRoutes from 'app/utils/useRoutes';
import { useRouter } from 'next/navigation';

export const useImpersonation = () => {
  const router = useRouter();
  const ROUTES = useRoutes();
  const { clearUserLoginResponse } = useToken();
  const origin = useImpersonationStore(state => state.origin);
  const setOrigin = useImpersonationStore(state => state.setOrigin);
  const clearOrigin = useImpersonationStore(state => state.clearOrigin);
  const setExpiredNotice = useImpersonationStore(
    state => state.setExpiredNotice
  );
  const setLoginUser = useSessionStore(state => state.setLoginUser);
  const setOperatorLogged = useSessionStore(state => state.setOperatorLogged);

  const isImpersonating = !!origin;

  const start = async (userId: string): Promise<void> => {
    const current = useSessionStore.getState().loginUser;
    if (!current) throw new Error('No active session');

    const impersonated = await userService.impersonate(userId, current.token);
    setOrigin({
      loginUser: current,
      operatorLogged: useSessionStore.getState().operatorLogged,
    });
    setLoginUser(impersonated);
    setOperatorLogged(undefined);
    router.push(ROUTES.menu);
  };

  const stop = async (expired = false): Promise<void> => {
    const savedOrigin = useImpersonationStore.getState().origin;
    if (!savedOrigin) return;

    const originExpiryTimestamp = new Date(
      savedOrigin.loginUser.refreshTokenExpiryTime
    ).getTime();
    const isOriginValid = originExpiryTimestamp > Date.now();

    const restoredUser = isOriginValid
      ? await (savedOrigin.loginUser.token
          ? userService.getMe(savedOrigin.loginUser.token)
          : userService.getById(savedOrigin.loginUser.agentId)
        ).catch(() => null)
      : null;

    if (!isOriginValid || !restoredUser || !restoredUser.active) {
      clearOrigin();
      clearUserLoginResponse();
      router.replace(ROUTES.home);
      return;
    }

    setLoginUser({
      ...savedOrigin.loginUser,
      role: restoredUser.role,
      permissions: restoredUser.permissions ?? [],
      userLevel: restoredUser.userLevel,
    });
    setOperatorLogged(savedOrigin.operatorLogged);
    clearOrigin();
    router.push(ROUTES.users);
    if (expired) setExpiredNotice(true);
  };

  const discard = (): void => {
    clearOrigin();
  };

  return { isImpersonating, origin, start, stop, discard };
};
